import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';

import { ChatComposer } from '@/components/chat/ChatComposer.web';
import { ChatContextBin } from '@/components/chat/ChatContextBin.web';
import { ChatDebugPane } from '@/components/chat/ChatDebugPane.web';
import { ChatMessageList } from '@/components/chat/ChatMessageList.web';
import { ChatModelPicker } from '@/components/chat/ChatModelPicker.web';
import { ChatUsageMeter } from '@/components/chat/ChatUsageMeter.web';
import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { Text } from '@/components/ui';
import {
  getConversation,
  getConversationDebugPrompt,
  patchConversation,
} from '@/lib/chat/api';
import { setLastChatId } from '@/lib/chat/last-conversation';
import type { ContextSet, Conversation, Message } from '@/lib/chat/types';
import { buildDebugTurns } from '@/lib/chat/turn-debug';
import type { ChatTurnDebugTrace } from '@/lib/chat/turn-debug';
import {
  CHAT_STREAM_COMPLETED,
  startConversationStream,
  stopConversationStream,
  toggleStreamLiveActivityCollapsed,
  useConversationStreamSnapshot,
  useConversationStreaming,
} from '@/lib/chat/stream-registry';
import { isOnline } from '@/lib/connectivity';

type ChatConversationProps = {
  conversationId?: string;
};

export function ChatConversation({ conversationId: conversationIdProp }: ChatConversationProps) {
  const { id } = useLocalSearchParams<{ id: string }>();
  const conversationId =
    conversationIdProp ?? (typeof id === 'string' ? id : id?.[0]);

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [debugExpanded, setDebugExpanded] = useState(false);
  const [debugTurns, setDebugTurns] = useState<ChatTurnDebugTrace[]>([]);

  const contextSetRef = useRef<ContextSet | null>(null);
  const instructionsRef = useRef<string | null>(null);
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const streaming = useConversationStreaming(conversationId);
  const streamSnapshot = useConversationStreamSnapshot(conversationId);

  const offline = !isOnline();

  const load = useCallback(async () => {
    if (!conversationId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getConversation(conversationId);
      setConversation(data.conversation);
      setMessages(data.messages);
      setDebugTurns(buildDebugTurns(data.messages));
      contextSetRef.current = data.conversation.contextSet;
      instructionsRef.current = data.conversation.instructions;
      setLastChatId(conversationId);
    } catch (err) {
      setConversation(null);
      setMessages([]);
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setConversation(null);
    setMessages([]);
    setDebugTurns([]);
    setError(null);
    void load();
  }, [conversationId, load]);

  useEffect(() => {
    function onStreamCompleted(event: Event) {
      const detail = (event as CustomEvent<{ conversationId: string; aborted: boolean }>).detail;
      if (detail.conversationId !== conversationId || detail.aborted) {
        return;
      }
      void load();
    }

    window.addEventListener(CHAT_STREAM_COMPLETED, onStreamCompleted);
    return () => window.removeEventListener(CHAT_STREAM_COMPLETED, onStreamCompleted);
  }, [conversationId, load]);

  const schedulePatch = useCallback(
    (patch: { contextSet?: ContextSet; instructions?: string | null; model?: string; title?: string | null }) => {
      if (!conversationId) {
        return;
      }
      if (patchTimerRef.current) {
        clearTimeout(patchTimerRef.current);
      }
      patchTimerRef.current = setTimeout(() => {
        void patchConversation(conversationId, patch)
          .then((updated) => {
            setConversation(updated);
            contextSetRef.current = updated.contextSet;
            instructionsRef.current = updated.instructions;
          })
          .catch((err) => {
            setError(err instanceof Error ? err.message : 'Failed to save conversation');
          });
      }, 400);
    },
    [conversationId],
  );

  const displayMessages = useMemo(() => {
    if (!streamSnapshot) {
      return messages;
    }
    const optimisticId = streamSnapshot.optimisticUserMessage.id;
    if (messages.some((message) => message.id === optimisticId)) {
      return messages;
    }
    return [...messages, streamSnapshot.optimisticUserMessage];
  }, [messages, streamSnapshot]);

  const lastAssistant = [...displayMessages].reverse().find((message) => message.role === 'assistant') ?? null;
  const streamError = streamSnapshot?.streamError ?? null;

  function onContextSetChange(next: ContextSet) {
    contextSetRef.current = next;
    setConversation((prev) => (prev ? { ...prev, contextSet: next } : prev));
    schedulePatch({ contextSet: next });
  }

  function onInstructionsChange(next: string) {
    instructionsRef.current = next;
    setConversation((prev) => (prev ? { ...prev, instructions: next || null } : prev));
    schedulePatch({ instructions: next || null });
  }

  function onModelChange(model: string) {
    setConversation((prev) => (prev ? { ...prev, model } : prev));
    schedulePatch({ model });
  }

  function onStopStream() {
    if (conversationId) {
      stopConversationStream(conversationId);
    }
  }

  async function onSend(text: string) {
    if (!conversationId || offline || streaming) {
      return;
    }

    if (patchTimerRef.current) {
      clearTimeout(patchTimerRef.current);
      patchTimerRef.current = null;
    }

    await startConversationStream(conversationId, text, {
      prepare: async () => {
        if (!contextSetRef.current) {
          return;
        }
        try {
          await patchConversation(conversationId, {
            contextSet: contextSetRef.current,
            instructions: instructionsRef.current,
          });
        } catch {
          // continue; server may still accept turn
        }
      },
      onDebugTrace: (trace) => {
        setDebugTurns((prev) => [...prev, trace]);
      },
    });
  }

  if (!conversationId) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <Text variant="body" className="text-muted">
          Missing conversation id
        </Text>
      </View>
    );
  }

  if (loading && !conversation) {
    return (
      <View className="chat-pane items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error && !conversation) {
    return (
      <View className="chat-pane items-center justify-center px-6">
        <Text variant="body" className="text-danger">
          {error}
        </Text>
      </View>
    );
  }

  const title = conversation?.title?.trim() || 'New conversation';

  return (
    <View className={`chat-pane${debugExpanded ? ' chat-pane-debug-open' : ''}`}>
      <DocumentTitle title={title} />
      <View className="flex-row items-center justify-between border-b border-border px-page py-2">
        <Text variant="h3" className="min-w-0 flex-1 truncate">
          {title}
        </Text>
        {conversation ? (
          <ChatModelPicker
            disabled={streaming || offline}
            value={conversation.model}
            onChange={onModelChange}
          />
        ) : null}
      </View>

      {offline ? (
        <View className="border-b border-border bg-surface px-page py-2">
          <Text variant="caption" className="text-muted">
            Chat requires an internet connection.
          </Text>
        </View>
      ) : null}

      {streamError ? (
        <View className="border-b border-danger bg-danger-muted px-page py-2">
          <Text variant="caption" className="text-danger">
            {streamError}
          </Text>
        </View>
      ) : null}

      <ChatMessageList
        contextSet={conversation?.contextSet ?? { discovery: 'open', pins: [] }}
        liveActivities={streamSnapshot?.liveActivities ?? []}
        liveActivityCollapsed={streamSnapshot?.liveActivityCollapsed ?? false}
        messages={displayMessages}
        streamingText={streaming ? (streamSnapshot?.streamingText ?? '') : null}
        onToggleLiveActivity={() => {
          if (conversationId) {
            toggleStreamLiveActivityCollapsed(conversationId);
          }
        }}
      />

      <ChatComposer
        contextSet={conversation?.contextSet ?? { discovery: 'open', pins: [] }}
        disabled={offline}
        messages={displayMessages}
        sending={streaming}
        onContextSetChange={onContextSetChange}
        onSend={(text) => void onSend(text)}
        onStop={onStopStream}
      />

      {conversation ? (
        <ChatContextBin
          contextSet={conversation.contextSet}
          expanded={contextExpanded}
          instructions={conversation.instructions}
          usageLine={<ChatUsageMeter compact message={lastAssistant} />}
          onContextSetChange={onContextSetChange}
          onInstructionsChange={onInstructionsChange}
          onToggleExpanded={() => setContextExpanded((value) => !value)}
        />
      ) : null}

      <ChatDebugPane
        expanded={debugExpanded}
        turns={debugTurns}
        onToggleExpanded={() => setDebugExpanded((value) => !value)}
        onLoadPrompt={() => getConversationDebugPrompt(conversationId)}
      />
    </View>
  );
}
